<script context="module" lang="ts">
  import type { Api_Testimonial, Api_TestimonialRequest, TestimonialNewInput } from '$lib/types/api/graphql';

  import { writable, type Writable } from 'svelte/store';

  interface TestimonialParams {
    modal: 'survey' | 'viewer' | 'editor' | 'success';
    testimonial: Api_Testimonial;
    testimonialRequest: Partial<Api_TestimonialRequest>;
  }

  const testimonial: Writable<Api_Testimonial | null> = writable(null);
  const testimonialRequest: Writable<Api_TestimonialRequest | null> = writable(null);

  const isSurveyModalOpen = writable(false);
  const isEditorModalOpen = writable(false);
  const isSuccessModalOpen = writable(false);
  const isViewerModalOpen = writable(false);

  export function open(params: Partial<TestimonialParams> = {}) {
    if (params.modal === 'survey') {
      testimonialRequest.set({ ...(params?.testimonialRequest || {}) });
      isSurveyModalOpen.set(true);
    } else if (params.modal === 'viewer') {
      testimonial.set({ ...(params?.testimonial || {}) });
      isViewerModalOpen.set(true);
    } else if (params.modal === 'editor') {
      isEditorModalOpen.set(true);
    } else if (params.modal === 'success') {
      isSuccessModalOpen.set(true);
    }
  }
</script>

<script lang="ts">
  import { IRK_API_URL } from '$lib/constants';
  import { mutate } from '$lib/apollo';
  import { captureException } from '@sentry/sveltekit';
  import Suspense from '$components/Suspense.svelte';
  import { lazyComponent } from '$lib/utils';
  import { EDIT_TESTIMONIAL } from '$graphql/mutations';

  const TestimonialSurveyModalLazy = lazyComponent(() => import('./TestimonialSurveyModal.svelte'));
  const TestimonialEditorModalLazy = lazyComponent(() => import('./TestimonialEditorModal.svelte'));
  const TestimonialViewerModalLazy = lazyComponent(() => import('./TestimonialViewerModal.svelte'));
  const TestimonialSuccessModalLazy = lazyComponent(() => import('./TestimonialSuccessModal.svelte'));

  const testimonialNewInputDefault: TestimonialNewInput = {
    feedback: '',
    impression: null,
    sentiment: null
  };

  let testimonialNewInput: TestimonialNewInput = { ...testimonialNewInputDefault };

  async function hideTestimonialRequestModal() {
    $isSurveyModalOpen = false;

    // The request may not actually exist, so in this case we just dismiss without the mutation
    if (!$testimonialRequest?.id) {
      return;
    }

    try {
      await mutate({
        mutation: EDIT_TESTIMONIAL,
        variables: {
          input: {
            id: $testimonialRequest?.id,
            testimonial: {
              dismissedModal: true
            }
          }
        }
      });
    } catch (error) {
      captureException(error);
    }
  }
</script>

<Suspense condition={$isSurveyModalOpen} promise={TestimonialSurveyModalLazy} let:component={TestimonialSurveyModal}>
  <TestimonialSurveyModal
    seller={$testimonialRequest?.seller}
    bind:isOpen={$isSurveyModalOpen}
    bind:testimonialNewInput
    on:openEditor={() => {
      $isSurveyModalOpen = false;
      $isEditorModalOpen = true;
    }}
    on:close={hideTestimonialRequestModal}
    on:done={() => {
      $isSurveyModalOpen = false;
      $isSuccessModalOpen = true;
    }}
  />
</Suspense>

<Suspense condition={$isEditorModalOpen} promise={TestimonialEditorModalLazy} let:component={TestimonialEditorModal}>
  <TestimonialEditorModal
    testimonialRequest={$testimonialRequest}
    bind:isOpen={$isEditorModalOpen}
    bind:testimonialNewInput
    on:done={() => {
      $isSuccessModalOpen = true;
    }}
  />
</Suspense>

<Suspense condition={$isViewerModalOpen} promise={TestimonialViewerModalLazy} let:component={TestimonialViewerModal}>
  <TestimonialViewerModal testimonial={$testimonial} bind:isOpen={$isViewerModalOpen} bind:testimonialNewInput />
</Suspense>

<Suspense condition={$isSuccessModalOpen} promise={TestimonialSuccessModalLazy} let:component={TestimonialSuccessModal}>
  <TestimonialSuccessModal
    bind:isOpen={$isSuccessModalOpen}
    testimonialRequest={$testimonialRequest}
    {testimonialNewInput}
  />
</Suspense>
